import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner, usePermissions } from "@/hooks/use-permissions";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { VendorCreditCard, Property, PropertyCardAssignment } from "@shared/schema";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { HostPaymentMethodsSection } from "@/components/host-payment-methods";
import { MercuryConnectedBadge, MercuryRecommendationCard } from "@/components/mercury-recommendation-card";
import { ACTIVE_STRIPE_MODE } from "@/lib/stripe-publishable-key";
import { usePageTitle } from "@/hooks/use-page-title";

export default function AppOperationsVendorSettings() {
  const queryClient = useQueryClient();
  usePageTitle("Vendor Settings");
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("maintenance");

  // Share the same cache key as HostPaymentMethodsSection so adding a card
  // in the unified section instantly populates the Property Assignments list
  // below — both ultimately read from the same `vendor_credit_cards` table.
  const cardsQuery = useQuery<VendorCreditCard[]>({
    queryKey: ["/api/app/billing/payment-methods"],
  });

  const propertiesQuery = useQuery<Property[]>({
    queryKey: ["/api/app/properties"],
  });

  const assignmentsQuery = useQuery<PropertyCardAssignment[]>({
    queryKey: ["/api/app/operations/property-card-assignments"],
  });

  const assignCardMutation = useMutation({
    mutationFn: async ({ propertyId, vendorCreditCardId }: { propertyId: number; vendorCreditCardId: number }) => {
      const res = await apiRequest("POST", "/api/app/operations/property-card-assignments", { propertyId, vendorCreditCardId });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/property-card-assignments"] });
    },
  });

  const LIVE_STATUSES = ["active", "launch", "renovations"];
  const cards = cardsQuery.data || [];
  const properties = (propertiesQuery.data || []).filter((p) => LIVE_STATUSES.includes(p.status));
  const assignments = assignmentsQuery.data || [];

  const getCardForProperty = (propertyId: number) => {
    const assignment = assignments.find((a) => a.propertyId === propertyId);
    if (assignment) return cards.find((c) => c.id === assignment.vendorCreditCardId);
    return cards.find((c) => c.isDefault);
  };

  const getCardLabel = (card: VendorCreditCard) => {
    return `${card.nickname || card.brand} •••• ${card.last4}`;
  };

  return (
    <AppLayout>
      <div className="p-6 max-w-4xl mx-auto" data-testid="page-vendor-settings">
        <ViewOnlyBanner area="maintenance" />
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="text-vendor-settings-title" data-tour="vendor-settings-heading">Vendor Credit Cards</h1>
            <p className="text-sm text-gray-500 mt-1">
              Cards on file are used to automatically pay vendors for work at the properties they're assigned to.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ExplainerVideoButton pageKey="vendor-settings" />
            <GuideButton pageKey="vendor-settings" />
          </div>
        </div>
        <GuideNudgeBanner pageKey="vendor-settings" />

        <div className="mb-8">
          <div className="flex items-center justify-end mb-2">
            <MercuryConnectedBadge />
          </div>
          <HostPaymentMethodsSection readOnly={readOnly} />
        </div>

        <MercuryRecommendationCard className="mb-8" />

        {properties.length > 0 && cards.length > 0 && (
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Property Assignments</h2>
            <p className="text-sm text-gray-500 mb-4">
              {cards.length === 1
                ? "Your default card is assigned to all properties."
                : "Assign a card to each property. Properties without a specific assignment will use the default card."}
            </p>
            <div className="border rounded-lg divide-y" data-tour="vendor-settings-categories">
              {properties.map((property) => {
                const assignedCard = getCardForProperty(property.id);
                const assignment = assignments.find((a) => a.propertyId === property.id);
                return (
                  <div key={property.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-3" data-testid={`row-property-card-${property.id}`}>
                    <span className="text-sm font-medium text-gray-700 break-words min-w-0">{property.name}</span>
                    {cards.length <= 1 ? (
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span className="break-words">{assignedCard ? getCardLabel(assignedCard) : "No card"}</span>
                      </div>
                    ) : (
                      <Select
                        value={assignment ? String(assignment.vendorCreditCardId) : "default"}
                        onValueChange={(v) => {
                          if (v === "default") {
                            const defaultCard = cards.find(c => c.isDefault);
                            if (defaultCard) assignCardMutation.mutate({ propertyId: property.id, vendorCreditCardId: defaultCard.id });
                            return;
                          }
                          assignCardMutation.mutate({ propertyId: property.id, vendorCreditCardId: parseInt(v) });
                        }}
                      >
                        <SelectTrigger className="w-full sm:w-[220px] h-8 text-sm" data-testid={`select-card-property-${property.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">Use default card</SelectItem>
                          {cards.map((card) => (
                            <SelectItem key={card.id} value={String(card.id)}>
                              {getCardLabel(card)}{card.isDefault ? " (Default)" : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <strong className="text-sm text-blue-800">Stripe Integration</strong>
            {ACTIVE_STRIPE_MODE === "test" && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300">
                Test mode — no real charges
              </span>
            )}
            {ACTIVE_STRIPE_MODE === "live" && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-800 border border-green-300">
                Live
              </span>
            )}
          </div>
          <p className="text-sm text-blue-800">
            When Stripe keys are configured, card entry will use secure Stripe Elements
            and payments will be processed via Stripe Connect destination charges. The active mode
            is controlled by the <code className="text-xs bg-blue-100 px-1 py-0.5 rounded">STRIPE_MODE</code> environment variable.
          </p>
        </div>
      </div>
    </AppLayout>
  );
}
